/**
 * Buy & Sell Used Furniture in India — Guide Page
 * Route: /buy-used-furniture
 * Purpose: High-value static content page for AdSense & SEO
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import {
  ArrowLeft,
  Sofa,
  CheckCircle2,
  AlertTriangle,
  IndianRupee,
  Truck,
  Search,
  ChevronRight,
  Ruler,
} from 'lucide-react';

const guideJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'How to Buy & Sell Used Furniture in India — Complete Guide',
  description: 'A practical guide to buying and selling second-hand furniture in India. Learn what to inspect, how to price, arrange delivery, and negotiate effectively.',
  author: { '@type': 'Organization', name: 'MemAds' },
  publisher: {
    '@type': 'Organization',
    name: 'MemAds',
    logo: { '@type': 'ImageObject', url: 'https://memads.in/android-chrome-192x192.png' },
  },
  datePublished: '2026-03-05',
  dateModified: '2026-08-01',
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://memads.in/buy-used-furniture' },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How do I arrange delivery for used furniture bought on MemAds?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most used furniture is sold as pickup-only. You can hire a local tempo or mini-truck through apps like Porter or Borzo. For smaller items, auto-rickshaws or hatchback taxis may work. Always measure your furniture and doorways before arranging transport. Disassembly of modular furniture (like IKEA or Godrej Interio pieces) usually reduces transport cost significantly.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the best time of year to buy used furniture in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The best time to find used furniture deals in India is during relocation seasons — March to May (summer transfers, new job relocations) and October to November (post-Diwali when people upgrade). During these periods, supply increases significantly and prices are more negotiable because sellers want to move items quickly.',
      },
    },
    {
      '@type': 'Question',
      name: 'How should I price my used furniture when selling online?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Price used furniture at 30–50% of its original purchase price for items 1–3 years old in good condition, and 15–30% for older items. Check similar listings on MemAds for your city to calibrate. Branded furniture from Godrej Interio, Urban Ladder, Pepperfry, or IKEA holds value better than unbranded pieces. Include good photos and precise dimensions in your listing to attract serious buyers.',
      },
    },
  ],
};

const BuyUsedFurniturePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background" id="buy-used-furniture-page">
      <SEO
        title="Buy & Sell Used Furniture in India — Complete Guide | MemAds"
        description="Learn how to buy and sell used furniture in India. Inspection tips, pricing guide, delivery advice, and how to find the best deals near you on MemAds classifieds."
        canonical="/buy-used-furniture"
        jsonLd={[guideJsonLd, faqJsonLd]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-amber-950 to-orange-900 py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(245,158,11,0.15),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
              <Sofa className="h-6 w-6 text-amber-400" />
            </div>
            <span className="text-xs font-semibold text-amber-400 uppercase tracking-widest">Buyer's & Seller's Guide</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4">
            Buy & Sell Used Furniture in India — Complete Guide
          </h1>
          <p className="text-base text-white/70 max-w-2xl leading-relaxed">
            Whether you're furnishing a new home on a budget or decluttering before a move, India's used furniture market offers enormous value. Learn how to buy safely and sell quickly.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12 space-y-12">

        {/* Intro */}
        <section id="why-buy-used-furniture">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-primary" />
            Why Buy Used Furniture?
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
            <p>
              New furniture is expensive and depreciates the moment it enters your home. A sofa set that costs ₹40,000 new can be purchased for ₹12,000–₹18,000 second-hand in excellent condition. For rental accommodation, short-term stays, or first apartments, buying used furniture is a highly practical choice.
            </p>
            <p>
              India's used furniture market is driven largely by high urban mobility — professionals relocating for work, students changing cities, and families upgrading to larger homes all generate a steady supply of quality pre-owned furniture listed at significant discounts.
            </p>
            <p>
              Popular categories in used furniture include: sofa sets, beds and mattresses, dining tables, wardrobes and almirahs, office chairs and study tables, bookshelves, and kitchen appliances (which technically fall under home goods but are sold alongside furniture). Branded pieces from Urban Ladder, Godrej Interio, IKEA, Pepperfry, and Nilkamal typically retain value better and are easier to resell.
            </p>
          </div>
        </section>

        {/* Inspection Checklist */}
        <section id="furniture-inspection-checklist" className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Furniture Inspection Checklist
          </h2>
          <p className="text-xs text-muted-foreground mb-5">Always inspect in person before buying — photos don't show everything:</p>
          <div className="space-y-2.5">
            {[
              { label: 'Check all joints and connections — test stability by applying light pressure from different angles', critical: true },
              { label: 'Inspect upholstery for stains, tears, sagging, or pet hair/smell', critical: true },
              { label: 'Test drawers and doors — they should slide/open smoothly without jamming', critical: false },
              { label: 'Check for termite or woodborer damage — look for fine powder under wooden pieces', critical: true },
              { label: 'Inspect metal parts for rust or corrosion, especially on bed frames and almirah hinges', critical: false },
              { label: 'Check mattress for sag, hygiene, and smell — flip it over and inspect both sides', critical: true },
              { label: 'Measure precisely and verify the piece will fit through your doorways and into your room', critical: true },
              { label: 'Verify screws, brackets, and fittings are all present for modular furniture assembly', critical: false },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-background border border-border">
                <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${item.critical ? 'text-rose-500' : 'text-green-500'}`} />
                <span className="text-sm text-foreground">
                  {item.label}
                  {item.critical && <span className="ml-2 text-xs font-semibold text-rose-500 bg-rose-50 dark:bg-rose-950/30 px-1.5 py-0.5 rounded">Critical</span>}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Measurement Tips */}
        <section id="furniture-measurements">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Ruler className="h-5 w-5 text-primary" />
            Measurement & Fit Tips
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
            <p>
              One of the most common mistakes when buying used furniture is not measuring properly. Arriving with a rented truck only to find the almirah doesn't fit through your front door is both frustrating and costly.
            </p>
            <ul className="space-y-2 not-prose">
              {[
                'Measure your doorway width, hallway width, and staircase turning radius before viewing any furniture.',
                'Standard Indian flat doorways are 28–32 inches wide. Most almirahs (72"H x 48"W x 21"D) require the door to be removed temporarily for entry.',
                'Measure your room and create a rough floor plan. Allow at least 90 cm of clearance in front of wardrobes and 60 cm around dining tables.',
                'For bed frames, measure the room accounting for side table space (minimum 50 cm on each side).',
                'When asking the seller for dimensions, request Length x Width x Height in centimetres or inches — avoid "double bed size" descriptions which vary.',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <ChevronRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Pricing Guide */}
        <section id="furniture-pricing">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-primary" />
            Pricing Guide for Common Items
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-3 font-semibold text-foreground border border-border">Item</th>
                  <th className="text-left p-3 font-semibold text-foreground border border-border">New Price Range</th>
                  <th className="text-left p-3 font-semibold text-foreground border border-border">Used Price (Good Condition)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { item: '3-seater sofa', new: '₹18,000–₹60,000', used: '₹6,000–₹20,000' },
                  { item: 'Queen size bed frame', new: '₹12,000–₹40,000', used: '₹4,000–₹15,000' },
                  { item: 'Double mattress (Springfit/Sleepwell)', new: '₹10,000–₹25,000', used: '₹3,000–₹10,000' },
                  { item: '6-door wooden almirah', new: '₹20,000–₹50,000', used: '₹7,000–₹18,000' },
                  { item: '4-seater dining table with chairs', new: '₹15,000–₹45,000', used: '₹5,000–₹18,000' },
                  { item: 'Study table with chair', new: '₹6,000–₹18,000', used: '₹2,000–₹7,000' },
                  { item: 'Refrigerator (double door)', new: '₹20,000–₹60,000', used: '₹7,000–₹22,000' },
                  { item: 'Washing machine (front load)', new: '₹22,000–₹55,000', used: '₹8,000–₹20,000' },
                ].map((row) => (
                  <tr key={row.item} className="border border-border hover:bg-muted/20 transition">
                    <td className="p-3 text-foreground font-medium">{row.item}</td>
                    <td className="p-3 text-muted-foreground">{row.new}</td>
                    <td className="p-3 text-green-600 font-semibold">{row.used}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">*Prices are indicative and vary by city, brand, age, and condition. Always compare live listings on MemAds.</p>
        </section>

        {/* Delivery Tips */}
        <section id="furniture-delivery" className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            Arranging Delivery & Transport
          </h2>
          <div className="space-y-3">
            {[
              { title: 'Hire a tempo / mini-truck', desc: 'Apps like Porter, Borzo, or local tempo services can move large furniture for ₹500–₹2,000 within a city. Always negotiate the rate including loading and unloading.' },
              { title: 'Disassemble modular furniture', desc: 'Modular pieces (wardrobes, bed frames, bookshelves) can often be disassembled to reduce truck size requirements. Ask the seller for the original assembly manual if available.' },
              { title: 'Plan timing around traffic', desc: 'Heavy traffic makes tempo loading hazardous and costly. Book transport for early morning (6–9am) on weekdays for the most affordable and efficient moves.' },
              { title: 'Factor transport into negotiations', desc: 'If the seller is far away, factor transport cost into your price negotiation. A sofa priced ₹1,000 cheaper may cost more overall if transport is ₹2,000 extra.' },
            ].map((tip) => (
              <div key={tip.title} className="flex gap-3">
                <Truck className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{tip.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Selling Tips */}
        <section id="furniture-selling-tips">
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Tips for Selling Your Furniture Fast
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
            <p>
              Furniture listings with clear, well-lit photographs and accurate dimensions sell 3–4x faster than those without. Here's how to maximise your chances of a quick sale on MemAds:
            </p>
            <ul className="space-y-2 not-prose">
              {[
                'Photograph in natural daylight — open curtains and move the item near a window if possible.',
                'Shoot from multiple angles: front, side, top, close-up of upholstery/material, and any imperfections.',
                'Always include exact dimensions (L x W x H in cm) in your listing description.',
                'Mention the brand, material, and age prominently — "Godrej Interio 6-door almirah, solid wood, 3 years old" performs much better than "big cupboard."',
                'Be transparent about condition — minor scratches or stains mentioned honestly build trust and pre-qualify serious buyers.',
                'Price 5–10% above your minimum to leave negotiation room. Buyers expect to negotiate.',
                'List during peak relocation seasons (March–May, October–November) for maximum visibility.',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <ChevronRight className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Scam Warning */}
        <section id="furniture-scams" className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Things to Watch Out For
          </h2>
          <div className="space-y-3">
            {[
              { title: 'Termite or woodborer damage', desc: 'Look for small round holes in wooden surfaces and fine powder beneath the furniture. This damage spreads to other wooden items in your home if not treated immediately.' },
              { title: 'Advance payment before viewing', desc: 'Never pay any amount — not even a ₹500 "token" — without physically inspecting the furniture first. Always meet at the seller\'s location and pay only when satisfied.' },
              { title: 'Photos vs reality mismatch', desc: 'Sellers sometimes use manufacturer photos. Always ask for live photos with the item next to a common reference object (like a water bottle or door) for size comparison.' },
            ].map((scam) => (
              <div key={scam.title} className="flex gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{scam.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{scam.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section id="used-furniture-faq">
          <h2 className="text-2xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqJsonLd.mainEntity.map((faq, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-semibold text-sm text-foreground mb-2">{faq.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-200 dark:border-amber-800 rounded-2xl p-8 text-center">
          <Sofa className="h-8 w-8 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-foreground mb-2">Find Used Furniture Near You</h2>
          <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
            Browse thousands of sofas, beds, dining tables, almirahs, and more — from verified local sellers across India.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/listings?category=furniture"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-6 py-3 text-sm font-semibold text-white shadow-md hover:brightness-105 transition"
              id="browse-used-furniture-cta"
            >
              <Sofa className="h-4 w-4" /> Browse Used Furniture
            </Link>
            <Link
              to="/post"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground hover:bg-accent transition"
              id="sell-furniture-cta"
            >
              Sell Your Furniture
            </Link>
          </div>
        </section>

        {/* Related */}
        <section id="related-furniture-guides">
          <h3 className="text-base font-semibold text-foreground mb-3">Related Guides</h3>
          <div className="flex flex-wrap gap-2">
            <Link to="/buy-used-cars" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Buy Used Cars Guide
            </Link>
            <Link to="/buy-used-mobiles" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Buy Used Mobiles Guide
            </Link>
            <Link to="/safety" className="inline-flex items-center gap-1.5 text-xs text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition">
              <ChevronRight className="h-3 w-3" /> Safety Tips
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BuyUsedFurniturePage;
